import React from 'react';
import { FormikProvider, Form, useFormik } from 'formik';
import * as Yup from 'yup';
import { Modal, Button, ButtonGroup, Shelf } from '@chaser/components';

import SelectableCard from '../../../../components/SelectableCard/index';
import usePush from '../../../../hooks/usePush';
import ConnectService from '../../../../services/connect-service';
import { pendo } from '../../../../util/logging';

const SAGE200_INTEGRATION_TYPE = {
  STANDARD: 'sage200-standard',
  PROFESSIONAL: 'sage200-professional',
};

const Sage200Modal = ({ open, close, onClosed }) => {
  const { execute, loading } = usePush(ConnectService.connectSage200, {
    errorToast: 'Failed to connect',
  });

  const form = useFormik({
    initialValues: {
      integrationType: SAGE200_INTEGRATION_TYPE.PROFESSIONAL,
    },
    validate: ({ integrationType }) => {
      const errors = {};
      if (!integrationType) errors.integrationType = 'Please select an integration type!';
      return errors;
    },
    validationSchema: Yup.object().shape({
      integrationType: Yup.string().required('You must select an integration type'),
    }),
    onSubmit: async ({ integrationType }, { setSubmitting }) => {
      const [err, url] = await execute({ integrationType });
      setSubmitting(false);

      if (err || !url) return;

      pendo.track('integration_modal_submitted', {
        integration: 'sage200',
        integrationType,
      });

      window.location = url;
    },
  });

  const INTEGRATION_TYPE_LABEL_MAP = {
    [SAGE200_INTEGRATION_TYPE.PROFESSIONAL]: 'Professional',
    [SAGE200_INTEGRATION_TYPE.STANDARD]: 'Standard',
  };

  const modalTitle = 'Select Sage 200 Integration Type';
  const buttonText = `Continue with ${INTEGRATION_TYPE_LABEL_MAP[form.values.integrationType]}`;

  return (
    <Modal title={modalTitle} open={open} toggle={close} onClosed={onClosed}>
      <FormikProvider value={form}>
        <Form>
          <Modal.Body>
            <Shelf
              role="group"
              gap="xlarge"
              justifyContent="center"
              aria-labelledby="sage200-integration-type"
            >
              <SelectableCard
                title="Professional"
                name="integrationType"
                description="Self-hosted, customizable ERP for advanced requirements."
                gap="xsmall"
                value={SAGE200_INTEGRATION_TYPE.PROFESSIONAL}
              />
              <SelectableCard
                title="Standard"
                name="integrationType"
                description="Cloud-based, streamlined business management solution."
                gap="xsmall"
                value={SAGE200_INTEGRATION_TYPE.STANDARD}
              />
            </Shelf>
          </Modal.Body>
          <Modal.Footer>
            <ButtonGroup>
              <Button disabled={!form.isValid} loading={loading} type="submit" data-trackingid="integration-sage200-connect">
                {buttonText}
              </Button>
            </ButtonGroup>
          </Modal.Footer>
        </Form>
      </FormikProvider>
    </Modal>
  );
};

export default Sage200Modal;
